import { zodResolver } from '@hookform/resolvers/zod';
import {
  AnchorButton,
  Button,
  Card,
  Checkbox,
  Conditional,
  Field,
  Form,
  RawInput as Input,
  Modal,
  RadioGroupField,
  SwitchCard,
  Tooltip,
  Text,
} from '@payaca/ambrosia';
import UntitledIcon from '@payaca/untitled-icons';
import { MutationKey, useIsMutating } from '@tanstack/react-query';
import {
  FC,
  memo,
  PropsWithChildren,
  RefObject,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import {
  Controller,
  useForm,
  useFormContext,
  UseFormReturn,
  useWatch,
} from 'react-hook-form';

import LineItemPriceCard from '@/ui/components/addEditItemSidebar/fields/LineItemPriceCard';
import LineItemTotalsCard from '@/ui/components/addEditItemSidebar/fields/LineItemTotalsCard';
import ReferenceField from '@/ui/components/addEditItemSidebar/fields/ReferenceField';
import ThumbnailField from '@/ui/components/addEditItemSidebar/fields/ThumbnailField';
import TitleDescriptionField from '@/ui/components/addEditItemSidebar/fields/TitleDescriptionField';
import LinkedMaterialsList from '@/ui/components/addEditItemSidebar/LinkedMaterialsList';
import {
  calculateMarkupChange,
  calculatePredictedMaterialCosts,
} from '@/ui/components/addEditItemSidebar/utils';
import { LineItemEditorData } from '@/ui/components/addEditProposalLineItem/defaultValues';
import {
  FORM_SCHEMA,
  TFormState,
  useDefaultValues,
} from '@/ui/components/addEditProposalLineItem/formState';
import AutoSaveForm, {
  AutoSaveFormHandle,
  AutoSaveFormProps,
} from '@/ui/components/autoSaveForm/AutoSaveForm';
import BuildMaterialsListSidebar, {
  MaterialState,
} from '@/ui/components/buildMaterialsListSidebar/BuildMaterialsListSidebar';
import ConfirmDeleteModal from '@/ui/components/confirm-delete-modal/ConfirmDeleteModal';
import PercentageField from '@/ui/components/percentageField/PercentageField';
import SidebarSplit from '@/ui/components/sidebarSplit/SidebarSplit';
import { Link, LinkProps } from 'react-router-dom';

type AutoSaveStatusProps = {
  isSaving: boolean;
  savingMutationKey?: MutationKey;
  hasUnsavedChanges: boolean;
  isValid: boolean;
};

/**
 * Distinguishes an unpersisted edit — still settling, or dropped for failing
 * validation — from a saved form, so a silently skipped invalid save is never
 * reported as "Auto saved".
 */
const AutoSaveStatus: FC<AutoSaveStatusProps> = (props) => {
  const { isSaving, savingMutationKey, hasUnsavedChanges, isValid } = props;

  const pendingSaveCount = useIsMutating({
    mutationKey: savingMutationKey,
    predicate: () => savingMutationKey !== undefined,
  });

  if (isSaving || pendingSaveCount > 0) {
    return (
      <div className="inline-flex items-center gap-1.5">
        <UntitledIcon name="loading-01.3" className="size-4 animate-spin" />
        <Text as="span">Saving...</Text>
      </div>
    );
  }

  if (hasUnsavedChanges && !isValid) {
    return (
      <div className="inline-flex items-center gap-1.5 text-red-600">
        <UntitledIcon name="alert-triangle.3" className="size-4" />
        <Text as="span">Couldn't save — check the highlighted fields</Text>
      </div>
    );
  }

  if (hasUnsavedChanges) {
    return (
      <div className="inline-flex items-center gap-1.5 text-gray-500">
        <UntitledIcon name="clock.3" className="size-4" />
        <Text as="span">Unsaved changes…</Text>
      </div>
    );
  }

  return (
    <div className="inline-flex items-center gap-1.5">
      <UntitledIcon name="check-square.3" className="size-4" />
      <Text as="span">Auto saved</Text>
    </div>
  );
};

/**
 * The thumbnail's `file` is dropped once uploaded, which isn't a change worth
 * saving; a newly picked file still registers through its `preview`.
 */
const toAutosaveComparable = ({ thumbnail, ...rest }: TFormState) => ({
  ...rest,
  thumbnail: thumbnail && { id: thumbnail.id, preview: thumbnail.preview },
});

const MemoisedLinkedMaterialsList = memo(
  LinkedMaterialsList<TFormState['materials'][number]>
);

const MemoisedBuildMaterialsListSidebar = memo(BuildMaterialsListSidebar);

const LINK_MATERIALS_WARN_USER_CONTENT = {
  body: "You have selected materials that you haven't linked to the item.",
};

const LineItemTotals: FC = () => {
  const { control } = useFormContext<TFormState>();
  const unitPriceExcTax = useWatch({ control, name: 'markupTotals.price' });

  return <LineItemTotalsCard unitPriceExcTax={unitPriceExcTax} />;
};

type LineItemFormProps = PropsWithChildren<{
  saveMode: AddEditProposalLineItemContentProps['saveMode'];
  methods: UseFormReturn<TFormState>;
  autoSaveRef: RefObject<AutoSaveFormHandle | null>;
  autosaveFieldDebounceMs?: AutoSaveFormProps<TFormState>['fieldDebounceMs'];
  onSubmit: (state: TFormState) => Promise<void>;
  onUnsavedChange: (hasUnsavedChanges: boolean) => void;
}>;

const LineItemForm: FC<LineItemFormProps> = (props) => {
  const {
    saveMode,
    methods,
    autoSaveRef,
    autosaveFieldDebounceMs,
    onSubmit,
    onUnsavedChange,
    children,
  } = props;

  if (saveMode === 'autosave') {
    return (
      <AutoSaveForm
        ref={autoSaveRef}
        methods={methods}
        className="flex h-full flex-col"
        fieldDebounceMs={autosaveFieldDebounceMs}
        getComparable={toAutosaveComparable}
        onUnsavedChange={onUnsavedChange}
        onSubmit={(state) => void onSubmit(state)}
      >
        {children}
      </AutoSaveForm>
    );
  }

  return (
    <Form
      methods={methods}
      className="flex h-full flex-col"
      onSubmit={onSubmit}
      onError={console.error}
    >
      {children}
    </Form>
  );
};

export type AddEditProposalLineItemContentProps = {
  proposalLineItemId?: string;
  proposalLineItemData?: LineItemEditorData;
  /**
   * `button` renders an explicit submit button and reports its dirty state via
   * `setIsDirty`, so a wrapping Sidebar can warn on close. `autosave` persists on
   * a debounce and hides the button, for the inline editor with no Sidebar.
   */
  saveMode: 'button' | 'autosave';
  /** Per-field autosave debounce windows, overriding `AutoSaveForm`'s default. */
  autosaveFieldDebounceMs?: AutoSaveFormProps<TFormState>['fieldDebounceMs'];
  /** Whether a save is in flight; drives the submit button and autosave status. */
  isSaving?: boolean;
  /**
   * Pending mutations under this key also show as saving in autosave mode,
   * without the parent re-rendering to pass `isSaving`.
   */
  savingMutationKey?: MutationKey;
  rootPage?: 'proposal' | 'invoice' | 'estimate';
  /** Whether to show the thumbnail field. Estimates don't render thumbnails. */
  showThumbnail?: boolean;
  allowProposalLineItemSelectionType?: boolean;
  isLinkMaterialSidebarOpen: boolean;
  setIsLinkMaterialSidebarOpen: (state: boolean) => void;
  setIsDirty?: (isDirty: boolean) => void;
  onSubmit: (state: TFormState) => Promise<void>;
  onThumbnailDelete: (thumbnailId: string) => Promise<void>;
  onDelete: () => Promise<void>;
  backTo?: LinkProps['to'];
};

const AddEditProposalLineItemContent: FC<
  AddEditProposalLineItemContentProps
> = (props) => {
  const {
    rootPage = 'proposal',
    showThumbnail = true,
    proposalLineItemId,
    proposalLineItemData,
    saveMode,
    autosaveFieldDebounceMs,
    isSaving = false,
    savingMutationKey,
    isLinkMaterialSidebarOpen,
    setIsLinkMaterialSidebarOpen,
    allowProposalLineItemSelectionType = false,
    setIsDirty,
    onSubmit,
    onThumbnailDelete,
    onDelete,
    backTo,
  } = props;

  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const stickyFooterRef = useRef<HTMLDivElement>(null);
  const [isDeletingLineItem, setIsDeletingLineItem] = useState(false);
  const [isUpdatingOriginal, setIsUpdatingOriginal] = useState(false);
  const [activeModal, setActiveModal] = useState<
    'CONFIRM_DELETE' | 'CONFIRM_UPDATE_ORIGINAL' | null
  >(null);
  const autoSaveRef = useRef<AutoSaveFormHandle>(null);
  const [hasUnsentChanges, setHasUnsentChanges] = useState(false);
  const [hasSaveFailed, setHasSaveFailed] = useState(false);

  const values = useDefaultValues(proposalLineItemData);
  const formMethods = useForm<TFormState>({
    resolver: zodResolver(FORM_SCHEMA),
    values,
    resetOptions: { keepDirtyValues: true },
    // So the save-status indicator can distinguish a pending save from one
    // dropped for being invalid.
    mode: saveMode === 'autosave' ? 'onChange' : 'onSubmit',
  });

  const { isDirty, isValid } = formMethods.formState;

  // Cascaded up because the form state must reset when the sidebar closes, which
  // needs `useForm` to unmount with it rather than live in the parent.
  useEffect(() => {
    setIsDirty?.(isDirty);
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useEffect has a missing dependency: 'setIsDirty'
  }, [isDirty]);

  // Mutating the class imperatively, so scrolling or typing in the form doesn't
  // re-render the whole editor just to recompute the shadow.
  useEffect(() => {
    const scrollEl = scrollContainerRef.current;
    const footerEl = stickyFooterRef.current;
    if (!scrollEl || !footerEl) return;

    const updateShadow = () => {
      const atBottom =
        scrollEl.scrollHeight - scrollEl.scrollTop - scrollEl.clientHeight <= 1;
      footerEl.classList.toggle('shadow-flipped-lg', !atBottom);
    };

    updateShadow();
    scrollEl.addEventListener('scroll', updateShadow, { passive: true });
    const resizeObserver = new ResizeObserver(updateShadow);
    resizeObserver.observe(scrollEl);
    const content = scrollEl.firstElementChild;
    if (content) resizeObserver.observe(content);

    return () => {
      scrollEl.removeEventListener('scroll', updateShadow);
      resizeObserver.disconnect();
    };
  }, []);

  const handleSubmit = async (state: TFormState) => {
    try {
      await onSubmit(state);
    } catch (error) {
      console.error(error);
    }
  };

  // Never resets the form after a save: edits typed while a save is in flight
  // must survive it, so the next autosave can send them.
  const saveItem = async (state: TFormState) => {
    try {
      await onSubmit(state);
      setHasSaveFailed(false);
      // So later saves don't upload the same file again — unless another file
      // was picked while this save was in flight.
      if (
        state.thumbnail?.file &&
        formMethods.getValues('thumbnail.file') === state.thumbnail.file
      ) {
        formMethods.resetField('thumbnail', {
          defaultValue: { ...state.thumbnail, file: undefined },
        });
      }
    } catch (error) {
      setHasSaveFailed(true);
      console.error(error);
    }
  };

  const handleThumbnailDelete = async () => {
    const thumbnailId = formMethods.getValues('thumbnail.id');

    // Not `undefined`: a Controller shows its mount-time value in place of an
    // undefined one, so a thumbnail loaded with the page would stay on screen.
    formMethods.setValue('thumbnail', null, {
      shouldDirty: true,
    });

    if (thumbnailId) {
      await onThumbnailDelete(thumbnailId);
    }
  };

  const handleDeleteItem = async () => {
    setIsDeletingLineItem(true);

    await onDelete();

    setIsDeletingLineItem(false);
    setActiveModal(null);
  };

  const handleUpdateOriginalClick = async () => {
    // Surface the errors on the form rather than opening the confirmation.
    const isFormValid = await formMethods.trigger();
    if (!isFormValid) return;

    setActiveModal('CONFIRM_UPDATE_ORIGINAL');
  };

  const handleConfirmUpdateOriginal = async () => {
    setIsUpdatingOriginal(true);
    // This explicit save (with the sync flag) carries the current values, so the
    // autosave must not send them again.
    autoSaveRef.current?.resetBaseline();

    try {
      await saveItem({
        ...formMethods.getValues(),
        updateOriginalLineItem: true,
      });
    } finally {
      setIsUpdatingOriginal(false);
      setActiveModal(null);
    }
  };

  // todo: dry with AddEditItemSidebar.tsx
  const handleLinkedMaterialsChange = useCallback(
    (newLinkedMaterials: TFormState['materials']) => {
      const predictedMaterialCosts = calculatePredictedMaterialCosts(
        newLinkedMaterials.map((material) => ({
          materialQuantity: material.quantity,
          // The backend orders the suppliers by 'preferred supplier' or lowest price
          price: material.suppliedBy[0]?.price.unitPriceExcTax.value || 0,
        }))
      );

      formMethods.setValue('materials', newLinkedMaterials, {
        shouldDirty: true,
      });

      let markupTotalsType = formMethods.getValues('markupTotals.markupType');
      let calculatedMarkupChange = calculateMarkupChange(
        markupTotalsType,
        formMethods.getValues(`markupTotals.${markupTotalsType}`),
        predictedMaterialCosts
      );

      if (newLinkedMaterials.length === 0) {
        markupTotalsType = 'price';
        calculatedMarkupChange = {
          profitPercentage: 0,
          profitAmount: 0,
          price: 0,
        };
      }

      formMethods.setValue(
        'markupTotals',
        {
          markupType: markupTotalsType,
          ...calculatedMarkupChange,
        },
        {
          shouldDirty: true,
        }
      );

      formMethods.setValue('predictedMaterialCost', predictedMaterialCosts, {
        shouldDirty: true,
      });
    },
    [formMethods]
  );

  const handleLinkMaterialsRequest = useCallback(
    () => setIsLinkMaterialSidebarOpen(true),
    [setIsLinkMaterialSidebarOpen]
  );

  const handleLinkMaterialsClose = useCallback(
    () => setIsLinkMaterialSidebarOpen(false),
    [setIsLinkMaterialSidebarOpen]
  );

  const handleAddMaterials = useCallback(
    (incomingMaterials: MaterialState[]) => {
      const merged = new Map(
        formMethods.getValues('materials').map((i) => [i['id'], i])
      );

      // Add or merge quantities with incoming materials
      incomingMaterials.forEach((i) => {
        merged.set(i['id'], {
          ...merged.get(i['id']),
          ...i,
          quantity: (merged.get(i['id'])?.quantity || 0) + i.quantity,
        });
      });

      handleLinkedMaterialsChange(Array.from(merged.values()));

      return Promise.resolve();
    },
    [formMethods, handleLinkedMaterialsChange]
  );

  const linkedMaterials = useWatch({
    control: formMethods.control,
    name: 'materials',
  });
  const canUpdateOriginalLineItem = useWatch({
    control: formMethods.control,
    name: 'canUpdateOriginalLineItem',
  });

  const numOfLinkedMaterials = linkedMaterials.reduce((acc, linkedMaterial) => {
    return acc + linkedMaterial.quantity;
  }, 0);

  return (
    <>
      <SidebarSplit
        leftTab={{ label: 'Item details' }}
        rightTab={{
          label: `Linked materials${numOfLinkedMaterials > 0 ? ` (${numOfLinkedMaterials})` : ''}`,
        }}
      >
        <div className="overflow-auto" ref={scrollContainerRef}>
          <LineItemForm
            saveMode={saveMode}
            methods={formMethods}
            autoSaveRef={autoSaveRef}
            autosaveFieldDebounceMs={autosaveFieldDebounceMs}
            onSubmit={saveMode === 'autosave' ? saveItem : handleSubmit}
            onUnsavedChange={setHasUnsentChanges}
          >
            <div className="flex flex-col gap-4 p-3.5">
              <div className="grid sm:grid-cols-4 gap-4">
                <ReferenceField className="sm:col-span-3" />

                <Field.Legacy>
                  <Field.Label>Quantity</Field.Label>

                  <Input
                    type="number"
                    step="any"
                    onWheel={(e) => e.currentTarget.blur()}
                    {...formMethods.register('quantity', {
                      valueAsNumber: true,
                    })}
                  />
                </Field.Legacy>
              </div>

              <TitleDescriptionField />

              <Conditional condition={allowProposalLineItemSelectionType}>
                <Field.Legacy name="selectionType">
                  <Field.Label>Selection type</Field.Label>

                  <Controller
                    name="selectionType"
                    render={({ field: { onChange, value } }) => {
                      return (
                        <RadioGroupField
                          value={value}
                          onChange={(selectionType) => {
                            onChange(selectionType);
                          }}
                          horizontal
                          options={[
                            { label: 'Required', value: 'REQUIRED' },
                            {
                              label: 'Multiple choice',
                              value: 'MULTIPLE_CHOICE',
                            },
                            { label: 'Optional', value: 'OPTIONAL' },
                          ]}
                        />
                      );
                    }}
                  />
                </Field.Legacy>
              </Conditional>

              <LineItemPriceCard
                hasLinkedMaterials={numOfLinkedMaterials > 0}
              />

              <Controller
                render={({ field: { onChange, value } }) => {
                  return (
                    <SwitchCard
                      label="Discount"
                      value={value}
                      onChange={onChange}
                    >
                      <Conditional condition={value}>
                        <div className="space-y-4 mt-4">
                          <Field.Legacy>
                            <Controller
                              render={({ field: { onChange, value } }) => {
                                return (
                                  <PercentageField
                                    value={value}
                                    onChange={onChange}
                                    minErrorMessages={[
                                      'Discount percentage must be 0 or more',
                                    ]}
                                    maxErrorMessages={[
                                      'Discount percentage must be 100 or less',
                                    ]}
                                  />
                                );
                              }}
                              name="discountPercentage"
                            />
                          </Field.Legacy>

                          <Field.Legacy>
                            <Field.Label>Discount description</Field.Label>

                            <Input
                              {...formMethods.register('discountDescription')}
                            />
                          </Field.Legacy>
                        </div>
                      </Conditional>
                    </SwitchCard>
                  );
                }}
                control={formMethods.control}
                name="hasDiscount"
              />

              <LineItemTotals />

              <Conditional condition={showThumbnail}>
                <ThumbnailField onDelete={handleThumbnailDelete} />
              </Conditional>
            </div>

            <div
              ref={stickyFooterRef}
              className="sticky bottom-0 z-10 mt-auto w-full p-3.5 bg-white"
            >
              {saveMode === 'button' ? (
                <>
                  <Conditional condition={canUpdateOriginalLineItem}>
                    <Tooltip
                      tooltipContent={
                        <p className="flex items-center">
                          <UntitledIcon
                            name="alert-triangle.3"
                            className="w-5 h-5 mr-1 text-yellow-500"
                          />
                          This will also update the linked materials on the
                          original item
                        </p>
                      }
                    >
                      <Checkbox
                        className="mb-4"
                        label="Update original item"
                        {...formMethods.register('updateOriginalLineItem')}
                      />
                    </Tooltip>
                  </Conditional>

                  <Button
                    className="w-full mb-2"
                    type="submit"
                    disabled={isSaving || !isDirty}
                    isProcessing={isSaving}
                  >
                    {proposalLineItemId ? 'Update item' : 'Create item'}
                  </Button>
                </>
              ) : (
                <>
                  <div className="flex gap-2 justify-between items-center mb-3.5">
                    <AutoSaveStatus
                      isSaving={isSaving}
                      savingMutationKey={savingMutationKey}
                      hasUnsavedChanges={hasUnsentChanges || hasSaveFailed}
                      isValid={isValid}
                    />

                    <Conditional condition={canUpdateOriginalLineItem}>
                      <Button
                        type="button"
                        variant="link"
                        onClick={handleUpdateOriginalClick}
                      >
                        Update original item
                      </Button>
                    </Conditional>
                  </div>

                  {!!backTo && (
                    <AnchorButton className="w-full mb-2" as={Link} to={backTo}>
                      Done
                    </AnchorButton>
                  )}
                </>
              )}

              <Button
                className="w-full"
                colour="negative"
                variant="secondary"
                onClick={() => setActiveModal('CONFIRM_DELETE')}
              >
                Remove item
              </Button>
            </div>
          </LineItemForm>
        </div>

        <div className="flex flex-col @screen-md/sidebarsplit:bg-gray-50 p-3.5 overflow-auto">
          <MemoisedLinkedMaterialsList
            titleText="Linked materials per unit"
            linkedMaterials={linkedMaterials}
            onLinkMaterialsRequest={handleLinkMaterialsRequest}
            onChange={handleLinkedMaterialsChange}
          />
        </div>
      </SidebarSplit>
      <ConfirmDeleteModal
        isOpen={activeModal === 'CONFIRM_DELETE'}
        onClose={() => setActiveModal(null)}
        onConfirmDelete={handleDeleteItem}
        destructiveVerb="remove"
        entityName={{
          singular: `item from this ${rootPage}`,
          plural: `items from this ${rootPage}`,
        }}
        isBusy={isDeletingLineItem}
      />
      <Modal
        isOpen={activeModal === 'CONFIRM_UPDATE_ORIGINAL'}
        title="Update original item"
        onClose={() => setActiveModal(null)}
      >
        <Modal.Body>
          <p>
            This will also update the original catalogue item (including its
            linked materials) with the changes you've made here.
          </p>
        </Modal.Body>

        <Modal.Footer>
          <Modal.Footer.Actions>
            <Button variant="secondary" onClick={() => setActiveModal(null)}>
              Cancel
            </Button>
            <Button
              onClick={handleConfirmUpdateOriginal}
              isProcessing={isUpdatingOriginal}
              disabled={isUpdatingOriginal}
            >
              Update original item
            </Button>
          </Modal.Footer.Actions>
        </Modal.Footer>
      </Modal>
      <MemoisedBuildMaterialsListSidebar
        title="Link materials"
        showPriceIncludingTax={false}
        emptyStatePromptText="Link materials to item"
        primaryActionText="Link materials to item"
        warnUserContent={LINK_MATERIALS_WARN_USER_CONTENT}
        zIndexLevel={2}
        onAddMaterials={handleAddMaterials}
        isOpen={isLinkMaterialSidebarOpen}
        onClose={handleLinkMaterialsClose}
      />
    </>
  );
};

export default AddEditProposalLineItemContent;
